/**
 * Task type helpers.
 *
 * A "Task" is a `(group_name, sub_group_name)` bucket of one or more
 * `LLMUsecaseInfo` rows. Single-objective tasks are a degenerate sub-group of
 * size 1; multi-objective tasks have N rows under the same `sub_group_name`,
 * each contributing one structured output to the same response.
 */

import { TaskType } from '@/shared/domain/taskTypes';
/**
 * @typedef {Object} TaskSubGroup
 * @property {string} sub_group_name - User-facing task name.
 * @property {Array<Object>} usecases - One LLMUsecaseInfo row per output objective.
 */

/**
 * Map a `task_type` enum value to a short, user-facing label used in tab strips,
 * pill chips, and section headers ("Classification", "Entity Extraction", "Summarization").
 */
export const getTaskTypeShortLabel = (taskType) => {
    switch (taskType) {
        case TaskType.classification:
            return 'Classification';
        case TaskType.NER:
        case TaskType.NER_WITH_RELATIONS:
            return 'Entity Extraction';
        case TaskType.summarization:
            return 'Summarization';
        case TaskType.contentGeneration:
            return 'Content Generation';
        default:
            return 'Task';
    }
};

/**
 * Like `getTaskTypeShortLabel`, but also distinguishes single-label from
 * multi-label classification ("Classification (Single-label)" /
 * "Classification (Multi-label)") so summary chips can convey that
 * distinction at a glance. Falls back to the plain short label for
 * non-classification tasks.
 */
export const getTaskTypeShortLabelForUsecase = (usecase) => {
    const taskType = usecase?.task_type;
    const baseLabel = getTaskTypeShortLabel(taskType);
    if (taskType !== TaskType.classification) return baseLabel;
    return usecase?.is_multilabel
        ? `${baseLabel} (Multi-label)`
        : `${baseLabel} (Single-label)`;
};

const TASK_DRAWER_META_SEP = ' · ';

/**
 * Sticky task-drawer subtitle: optional objective · shared task name · project · task type.
 * Order: sub-task (usecase) name, `sub_group_name`, project `group_name`, task type label.
 *
 * @param {Object} params
 * @param {Object|null} params.usecase - Active objective (`LLMUsecaseInfo`).
 * @param {Object|null} [params.subGroup] - Task bucket with `sub_group_name` / `usecases`.
 * @param {string} [params.projectGroupName] - Parent app `group_name` (project name).
 * @param {boolean} [params.isMultiObjective] - More than one usecase under the task.
 */
export const formatTaskDrawerMetaLine = ({
    usecase,
    subGroup = null,
    projectGroupName = '',
    isMultiObjective = false,
}) => {
    if (!usecase) return '';

    const typeLabel = getTaskTypeShortLabelForUsecase(usecase);
    const subGroupName = (subGroup?.sub_group_name || usecase.sub_group_name || '').trim();
    const project = (projectGroupName || '').trim();
    const objectiveName = (usecase.usecase_name || '').trim();

    const showObjectiveFirst =
        (isMultiObjective && Boolean(objectiveName)) ||
        (!isMultiObjective &&
            Boolean(objectiveName && (!subGroupName || objectiveName !== subGroupName)));

    const parts = [];
    if (showObjectiveFirst) {
        parts.push(objectiveName);
    }
    if (subGroupName) {
        parts.push(subGroupName);
    }
    if (project) {
        parts.push(project);
    }
    if (typeLabel) {
        parts.push(typeLabel);
    }

    return parts.join(TASK_DRAWER_META_SEP);
};

/**
 * Parent app `group_name` for a sub-group bucket (project name in the UI).
 *
 * @param {Array<Object>} allApps - From global state.
 * @param {Object|null} subGroup - Bucket with `usecases` / `sub_group_name`.
 * @returns {string}
 */
export const resolveProjectGroupNameForSubGroup = (allApps, subGroup) => {
    if (!subGroup || !Array.isArray(allApps) || allApps.length === 0) {
        return '';
    }
    for (const app of allApps) {
        if ((app.sub_groups || []).some((sg) => sg === subGroup)) {
            return (app.group_name || '').trim();
        }
    }
    const ids = new Set((subGroup.usecases || []).map((u) => u._id).filter(Boolean));
    for (const app of allApps) {
        for (const sg of app.sub_groups || []) {
            if (sg.sub_group_name !== subGroup.sub_group_name) continue;
            if ((sg.usecases || []).some((u) => ids.has(u._id))) {
                return (app.group_name || '').trim();
            }
        }
    }
    return '';
};

/**
 * Full drawer subtitle from usecase + sub-group + apps tree (task step drawer,
 * Playground drawer, etc.).
 *
 * @param {Object} params
 * @param {Object|null} params.usecase
 * @param {Object|null} [params.subGroup]
 * @param {Array<Object>} [params.allApps]
 */
export const buildTaskDrawerMetaLine = ({ usecase, subGroup = null, allApps = [] }) => {
    if (!usecase) return '';
    const projectGroupName = resolveProjectGroupNameForSubGroup(allApps, subGroup);
    const isMultiObjective = (subGroup?.usecases?.length || 0) > 1;
    return formatTaskDrawerMetaLine({
        usecase,
        subGroup,
        projectGroupName,
        isMultiObjective,
    });
};

/**
 * Convert a task sub-group into the per-objective tab descriptors used by
 * ObjectiveTabs.
 */
export const subGroupToObjectives = (subGroup) => {
    if (!subGroup || !Array.isArray(subGroup.usecases)) return [];
    return subGroup.usecases.map((uc) => ({
        usecaseId: uc._id,
        usecase: uc,
        key: uc.usecase_name,
        taskType: uc.task_type,
        shortLabel: getTaskTypeShortLabel(uc.task_type),
        displayName: uc.usecase_name || 'objective',
    }));
};
